import { useEffect, useState } from "react";
import client from "../api/client";

const fmt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const styles = {
  page: {
    fontSize: 11,
    height: "calc(100vh - 20px)",
    overflowY: "auto",
    overflowX: "hidden",
    padding: 0,
  },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontSize: 13,
    fontWeight: 700,
    margin: 0,
  },
  sub: {
    fontSize: 10,
    opacity: 0.85,
  },
  body: {
    padding: 10,
  },
  btn: {
    height: 28,
    fontSize: 11,
    borderRadius: 7,
    fontWeight: 600,
    padding: "3px 9px",
  },
  tableWrap: {
    maxHeight: "calc(100vh - 135px)",
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: {
    fontSize: 11,
    minWidth: 900,
    marginBottom: 0,
  },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  badge: {
    fontSize: 10,
    padding: "4px 7px",
    borderRadius: 20,
    fontWeight: 600,
  },
};

export default function InvoicesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await client.get("/sales/invoices");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      alert("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const statusClass = (status) => {
    const s = String(status || "").toLowerCase();

    if (s.includes("paid")) return "text-bg-success";
    if (s.includes("cancel")) return "text-bg-danger";
    if (s.includes("pending")) return "text-bg-warning";

    return "text-bg-secondary";
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <h6 style={styles.title}>
              <i className="bi bi-receipt-cutoff me-2"></i>
              Invoice List
            </h6>
            <div style={styles.sub}>
              Sales invoices, GST values and print actions
            </div>
          </div>

          <button
            className="btn btn-light btn-sm"
            style={styles.btn}
            onClick={load}
            disabled={loading}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            {loading ? "Loading" : "Refresh"}
          </button>
        </div>

        <div style={styles.body}>
          <div
            style={{
              fontSize: 10,
              color: "#6c757d",
              marginBottom: 6,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>
              <i className="bi bi-database me-1"></i>
              Total Invoices: <strong>{rows.length}</strong>
            </span>
            <span>Click View to print invoice</span>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  <th style={styles.th}>Invoice No</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Customer</th>
                  <th style={styles.th}>Supply</th>
                  <th style={styles.th} className="text-end">
                    Taxable
                  </th>
                  <th style={styles.th} className="text-end">
                    Tax
                  </th>
                  <th style={styles.th} className="text-end">
                    Grand Total
                  </th>
                  <th style={styles.th}>Status</th>
                  <th style={{ ...styles.th, width: 90 }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      Loading invoices...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => {
                    const id = r.Id ?? r.id;
                    const status = r.Status ?? r.status ?? "-";

                    return (
                      <tr key={id ?? i}>
                        <td style={{ ...styles.td, fontWeight: 700 }}>
                          {r.InvoiceNo ?? r.invoiceNo}
                        </td>

                        <td style={styles.td}>
                          {r.InvoiceDate ?? r.invoiceDate}
                        </td>

                        <td style={styles.td}>
                          {r.CustomerName ?? r.customerName}
                        </td>

                        <td style={styles.td}>
                          {r.SupplyType ?? r.supplyType ?? "-"}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {fmt(r.SubTotal ?? r.subTotal)}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {fmt(r.TaxAmount ?? r.taxAmount)}
                        </td>

                        <td
                          className="text-end"
                          style={{
                            ...styles.td,
                            fontWeight: 700,
                            color: "#0d6efd",
                          }}
                        >
                          ₹ {fmt(r.GrandTotal ?? r.grandTotal)}
                        </td>

                        <td style={styles.td}>
                          <span
                            className={`badge ${statusClass(status)}`}
                            style={styles.badge}
                          >
                            {status}
                          </span>
                        </td>

                        <td style={styles.td}>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            style={{
                              ...styles.btn,
                              height: 26,
                              padding: "2px 8px",
                            }}
                            onClick={() =>
                              window.open(`/invoice-print/${id}`, "_blank")
                            }
                          >
                            <i className="bi bi-eye me-1"></i>
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
